
/*******************************************************************************************
   _____            __              ____   ____                         .__               
  /  _  \   _______/  |________  ___\   \ /   /____   ____  __ __  _____|__|____    ____  
 /  /_\  \ /  ___/\   __\_  __ \/  _ \   Y   // __ \ /    \|  |  \/  ___/  \__  \  /    \ 
/    |    \\___ \  |  |  |  | \(  <_> )     /\  ___/|   |  \  |  /\___ \|  |/ __ \|   |  \
\____|__  /____  > |__|  |__|   \____/ \___/  \___  >___|  /____//____  >__(____  /___|  /
        \/     \/                                 \/     \/           \/        \/     \/ 
********************************************************************************************
Auth Context
********************************************************************************************

Author:     Nicholas Hamilton, PhD
Email:      nicholasehamilton@gmail.com
Date:       23rd April 2025

*******************************************************************************************/
import React                    from 'react';
import moment                   from 'moment';
import pick                     from 'lodash/pick';
import { Preferences }          from '@capacitor/preferences';
import { Browser }              from '@capacitor/browser'; // For in-app browser usage
import { App as CapApp }        from '@capacitor/app';
import { useDevice }            from './DeviceContext';

import config                   from '../config';
import { useAlert }             from 'contexts';
import { 
    useLock, 
    useClock, 
    usePrevious
}                               from 'hooks';
import { useHistory }           from 'react-router-dom';

const {
    rootUrl,
    auth0 : {
        nativeCallbackUri,
    }
} = config;

const memory                    = {};
const DEBUG                     = false;

const INTERVAL_MS               = moment.duration( 5, 'minutes').asMilliseconds();  //  5 minutes
const INTERVAL_MS_DEBUG         = moment.duration(10, 'seconds').asMilliseconds();  // 10 seconds
const INTERVAL_MS_RESET         = moment.duration(15, 'seconds').asMilliseconds();  // 10 seconds
const REFRESH_INTERVAL_MS       = (DEBUG ? INTERVAL_MS_DEBUG : INTERVAL_MS);        // 10 seconds or 5 minutes
const MAX_CODE_AGE_MS           = (DEBUG ? INTERVAL_MS_DEBUG : INTERVAL_MS);        // 10 seconds or 5 minutes
const APP_STATE_KEY             = 'appState';
const APP_PKCE_VERIFIER_KEY     = "pkceVerifier";
const APP_PKCE_VERIFIER_LENGTH  = 128;
const APP_PKCE_STATE_KEY        = "pkceState";
const APP_PKCE_STATE_LENGTH     = 16;
const KEYS              = {
    accessToken     : 'access_token',
    idToken         : 'id_token',
    refreshToken    : 'refresh_token',
    issuedAt        : 'issued_at',
    expiresAt       : 'expires_at',
};

/**
 * Ensures the issuer URL ends with a trailing slash.
 *
 * @param {string} issuer - The issuer URL to normalize.
 * @returns {string} The normalized issuer URL with a trailing slash.
 */
const normalizeIssuer = (issuer) => {
    const str = String(issuer || ''); // Coerce to string safely
    return str.endsWith('/') ? str : str + '/';
};

/**
 * Decodes a JSON Web Token (JWT) without verifying its signature.
 *
 * @param {string} token - The JWT string to decode.
 * @returns {{ header: object, payload: object, signature: string }} Decoded JWT parts.
 * @throws Will throw an error if the token is missing or malformed.
 */
const decodeJwt = (token) => {
    if (!token) throw new Error("No token provided");
    const [headerB64, payloadB64, signature] = token.split('.');
    if (!headerB64 || !payloadB64) {
        throw new Error("Invalid JWT structure");
    }
  
    const base64UrlDecode = (str) => {
        const padded = str.padEnd(str.length + (4 - str.length % 4) % 4, '=');
        const base64 = padded.replace(/-/g, '+').replace(/_/g, '/');
        const jsonStr = atob(base64);
        return JSON.parse(jsonStr);
    };
  
    return {
        header: base64UrlDecode(headerB64),
        payload: base64UrlDecode(payloadB64),
        signature,
    };
}

/**
 * Verifies a JWT signed with RS256 using a JWKS endpoint.
 *
 * @param {Object} params
 * @param {string} params.token - The JWT to verify.
 * @param {string} params.jwksUrl - URL to fetch the JSON Web Key Set.
 * @param {string} [params.audience] - Expected audience claim.
 * @param {string} [params.issuer] - Expected issuer.
 * @param {string} [params.azp] - Expected authorized party.
 * @returns {Promise<Object>} Decoded and verified JWT payload.
 * @throws Will throw if the token is invalid or verification fails.
 */
const verifyJwt = async ({ token, jwksUrl, audience, issuer, azp}) => {
    
    const { header, payload, signature } = decodeJwt(token);
  
    if (header.alg !== 'RS256') 
        throw new Error("Only RS256 supported");
  
    // Fetch the JWKS and find the key by `kid`
    const res           = await fetch(jwksUrl);
    const { keys }      = await res.json();
    const jwk           = keys.find(k => k.kid === header.kid);
  
    if (!jwk) 
        throw new Error("Public key not found in JWKS");
  
    // Import key
    const key           = await window.crypto.subtle.importKey(
        'jwk',
        jwk,
        { name: 'RSASSA-PKCS1-v1_5', hash: 'SHA-256' },
        false,
        ['verify']
    );
  
    // Verify signature
    const encoder       = new TextEncoder();
    const data          = encoder.encode(`${token.split('.')[0]}.${token.split('.')[1]}`);
    const signatureBuf  = Uint8Array.from(atob(signature.replace(/-/g, '+').replace(/_/g, '/')), c => c.charCodeAt(0));
    const now           = Math.floor(Date.now() / 1000);
    const valid         = await crypto.subtle.verify('RSASSA-PKCS1-v1_5', key, signatureBuf, data);

    // Validate Claims
    if (!valid) 
        throw new Error("Invalid signature");
    if (issuer      && normalizeIssuer(payload.iss) !== normalizeIssuer(issuer))  // Ignore trailing slash     
        throw new Error("Invalid issuer");
    if (audience    && payload.aud !== audience)    
        throw new Error("Invalid audience");
    if (azp         && payload.azp !== azp)         
        throw new Error('Invalid azp (Authorized Party)')
    if (payload.exp && payload.exp < now)
        throw new Error("Token expired");
  
    return payload;
}

/**
 * Generates a cryptographically secure random string using a URL-safe charset.
 *
 * @param {number} length - Length of the resulting string.
 * @returns {string} A random string of specified length.
 * @throws {Error} If crypto API is not available or length is invalid.
 */
const generateRandomString = (length) => {
    const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
    let result = '';
    const randomValues = new Uint8Array(length);
    window.crypto.getRandomValues(randomValues);
    for (let i = 0; i < randomValues.length; i++) {
      result += charset[randomValues[i] % charset.length];
    }
    return result;
}

/**
 * Generates a code challenge from a code verifier using SHA-256 and base64url encoding.
 *
 * @param {string} codeVerifier - A high-entropy cryptographically random string.
 * @returns {Promise<string>} A base64url-encoded SHA-256 hash of the code verifier.
 * @throws {Error} If the codeVerifier is not a string.
 */
const generateCodeChallenge =  async (codeVerifier) => {
    const encoder = new TextEncoder();
    const data = encoder.encode(codeVerifier);
    const digest = await window.crypto.subtle.digest('SHA-256', data);
    const base64Digest = btoa(String.fromCharCode(...new Uint8Array(digest)))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');
    return base64Digest;
}

/**
 * Logs an error to the console with a custom or default message prefix.
 *
 * @param {Error|string|any} err - The error or message to be logged. It can be an instance of `Error`, a string, or any other value.
 * @param {string} [customMessage='Error'] - The custom message prefix to prepend before the error. Defaults to 'Error'.
 *
 * @example
 * // Logs an error with the default prefix
 * logError(new Error('Something went wrong'));
 *
 * @example
 * // Logs an error with a custom message
 * logError(new Error('Something went wrong'), 'Data Fetch Error');
 */
const logError = (err, customMessage = 'Error') => {
    if (err instanceof Error) {
        console.error(`${customMessage}: ${err.message}`, err);
    } else if (typeof err === 'string') {
        console.error(`${customMessage}: ${err}`);
    } else {
        console.error(`${customMessage}:`, err);
    }
};

/**
 * Builds a map of Auth0 endpoints based on the provided domain.
 *
 * @param {string} domain - The Auth0 domain (e.g., "your-tenant.auth0.com").
 * @returns {Object} An object containing the issuer, JWKS, token, and authorize endpoint URLs.
 * @returns {string} return.issuer - The issuer URL.
 * @returns {string} return.jwks - The JWKS (JSON Web Key Set) endpoint URL.
 * @returns {string} return.token - The OAuth token endpoint URL.
 * @returns {string} return.authorize - The authorization endpoint URL.
 */
const BuildAuth0EndpointsMap = (domain) => ({
    issuer      : `https://${domain}`,
    jwks        : `https://${domain}/.well-known/jwks.json`,
    token       : `https://${domain}/oauth/token`, 
    authorize   : `https://${domain}/authorize`
});

// The Product Context 
const AuthContext = React.createContext(undefined);

/**
 * AuthProvider component that manages authentication tokens and handles
 * their storage, refresh, and context propagation.
 *
 * @component
 * @param {Object} props - The props object.
 * @param {React.ReactNode} props.children - The child components that require authentication context.
 * @param {string} props.domain - The authentication domain (e.g., Auth0 domain or OAuth server).
 * @param {string} props.clientId - The client ID registered with the authentication provider.
 * @param {string} props.cacheLocation - The client ID registered with the authentication provider.
 * @param {Object} [props.authorizationParams] - Additional parameters to customize the authorization request.
 * @param {boolean} [props.useRefreshTokens=false] - Whether to use refresh tokens to maintain session.
 * @param {boolean} [props.debug=false] - Debug mode
 * @param {Function} [props.onRedirectCallback] - Callback function invoked after redirect during login.
 *
 * @returns {JSX.Element} The context provider component that wraps child components.
 */
const AuthProvider = ({
    children,
    domain,
    clientId,
    authorizationParams,
    useRefreshTokens,
    onRedirectCallback : handleRedirectCallback = null,
    cacheLocation,
    debug = false
}) => {

    const {now : reseeingRefreshMoment}                         = useClock(INTERVAL_MS_RESET);
    const history                                               = useHistory();
    const { alert }                                             = useAlert();
    const { isNative,           isConnected }                   = useDevice();
    const { 
        acquire : acquireRefreshLock, 
        release : releaseRefreshLock
    }                                                           = useLock({name:"refreshLock"}); // RefreshLock
    const {
        acquire : acquireTokenLock,
        release : releaseTokenLock
    }                                                           = useLock({name:"tokenLock"}); // TokenLock
    const [ error,                  setError ]                  = React.useState(null);
    const [ isLoaded,               setIsLoaded]                = React.useState(false);
    const [ isLoading,              setIsLoading ]              = React.useState(false);
    const [ isRefreshing,           setIsRefreshing]            = React.useState(false);
    const [ isResettingRefreshTokens,setIsResettingRefreshTokens]   = React.useState(false);
    const [ authTokens,             _setAuthTokens ]            = React.useState(null);
    const [ user,                   setUser ]                   = React.useState(null);
    const [ isAuthenticated,        setIsAuthenticated ]        = React.useState(false);  
    const [ refreshingAt,           setRefreshingAt ]           = React.useState(undefined)
    const tokenPrev                                             = usePrevious(authTokens?.[KEYS.accessToken]);

    const authTokensRef                                         = React.useRef(authTokens);
    const refreshIntervalRef                                    = React.useRef(null);
    const processedCodeRef                                      = React.useRef(new Map());

    const includeAccessToken                                    = React.useMemo(() => true, [])
    const includeRefreshToken                                   = React.useMemo(() => Boolean(useRefreshTokens), [useRefreshTokens]);
    const includeIdToken                                        = React.useMemo(() => Boolean((authorizationParams?.scope || '').includes('openid')), [authorizationParams?.scope]);

    const [issuedAt, expiresAt]                                 = React.useMemo(() => {
        const convert           = value => Number.isFinite(value) ? moment.unix(value) : null;
        const issuedAtMoment    = convert(authTokens?.[KEYS.issuedAt]);
        const expiresAtMoment   = convert(authTokens?.[KEYS.expiresAt]);
        return [issuedAtMoment,expiresAtMoment];
    }, [authTokens])

    // Endpoints Map
    const endpointsMap = React.useMemo(() => (
        BuildAuth0EndpointsMap(domain)
    ), [domain]);

    React.useEffect(() => {
        const token = authTokens?.[KEYS.accessToken];
        if(token && !tokenPrev) 
            alert("User Authenticated",'success');
        if(!token && tokenPrev) 
            alert("User Logged Out",'error');
    },[tokenPrev, authTokens, alert])

    React.useEffect(() => {
        if(error && error?.message){
            alert(error?.message,'error')
        }
    },[error,alert])

    const setAuthTokens = React.useCallback((tokens) => {
        authTokensRef.current = tokens;
        _setAuthTokens(tokens);
    },[])

    const cleanupProcessedCodes = React.useCallback( () => {
        const now = moment();
        const filteredEntries = Array.from(processedCodeRef.current.entries()).filter(([_,timestamp]) => 
            now.diff(timestamp, 'milliseconds') <= MAX_CODE_AGE_MS
        );
        processedCodeRef.current = new Map(filteredEntries);
    }, []);

    const clearProcessedCodes = React.useCallback(() => {
        processedCodeRef.current = new Map();
    },[])

    const _setValue = React.useCallback(async (key,value) => {
        if (isNative) {
            await Preferences.set({ key, value });
        } else if (cacheLocation === 'localstorage') {
            localStorage.setItem(key, value);
        } else {
            memory[key] = value; // or memory.setItem(key, value) if you have a wrapper
        }
    }, [cacheLocation, isNative])

    const _getValue = React.useCallback(async (key) => {
        if (isNative) {
            const result = await Preferences.get({ key });
            return result?.value ?? null;
        } else if (cacheLocation === 'localstorage') {
            return localStorage.getItem(key);
        } else {
            return memory[key] ?? null; // or memory.getItem(key) if using a wrapper
        } 
    }, [isNative, cacheLocation]);

    const _deleteValue = React.useCallback(async (key) => {
        if (isNative) {
            await Preferences.remove({ key });
        } else if (cacheLocation === 'localstorage') {
            localStorage.removeItem(key);
        } else {
            delete memory[key]; // or memory.removeItem(key) if using a wrapper
        }
    }, [isNative, cacheLocation]);

    /**
     * Function to verify the token and return the decoded payload.
     * 
     * @param {string} token - The token to validate.
     * @param {string} expectedAudience - The audience to validate.
     * @param {string} expectedAzp - The Authorized party to validate.
     * @returns {Promise<Object>} - Returns a Promise that resolves to the decoded payload if the token is valid.
     * @throws {Error} - Throws an error if the token is invalid or verification fails.
     */
    const verifyToken = React.useCallback( async (token, expectedAudience = null, expectedAzp = null) => {
        try {
            // Fetch the JWKS (JSON Web Key Set) from the issuer's URL
            const jwks          = await fetch(endpointsMap.jwks).then(response => response.json());
        
            // Decode the JWT header to extract the kid (key ID)
            const decodedHeader = decodeJwt(token).header;
            const kid           = decodedHeader.kid;
        
            // Find the matching JWK (JSON Web Key) for the kid
            const jwk = jwks.keys.find(key => key.kid === kid);
            if (!jwk) {
                throw new Error("Unable to find the key with the provided kid.");
            }

            const payload = await verifyJwt({
                token, 
                jwksUrl : endpointsMap.jwks, 
                issuer  : endpointsMap.issuer,
                ...(expectedAudience && {
                    audience    : expectedAudience
                }),
                ...(expectedAzp && {
                    azp         : expectedAzp
                })
            })
        
            return payload; // Return the verified JWT payload
      
        } catch (error) {
            setError(error);
            logError(error,"JWT verification failed")
            throw error
        }

    }, [endpointsMap.issuer, endpointsMap.jwks])

    /**
     * Clears authentication tokens from both persistent storage and React state.
     * Ensures exclusive access using a token lock.
     */
    const clearTokens = React.useCallback(async () => {

        try {
            
            await acquireTokenLock();

            // Remove tokens from storage (cookies or Preferences)
            await Promise.all(Object.values(KEYS).map(_deleteValue));

            // Clear tokens from state
            setAuthTokens(null);
            setUser(null);
            setIsAuthenticated(false);

            return null;

        } catch (err) {
            setError(err)
            
        } finally {
            releaseTokenLock();
        }
    }, [_deleteValue, acquireTokenLock, releaseTokenLock, setAuthTokens]);

    /**
     * Logs out the user by clearing tokens and redirecting.
     * 
     * @param {Object} [options] - Optional configuration.
     * @param {Object} [options.logoutParams] - Logout parameters.
     * @param {string} [options.logoutParams.returnTo] - URL to redirect to after logout.
     */
    const logout = React.useCallback(async (options) => {

        try {

            const returnTo = options?.logoutParams?.returnTo || "/";

            // Clear all tokens from storage
            setIsLoading(true);
            await new Promise(res => setTimeout(res,500));
            await clearTokens();
    
            // Platform-specific redirect
            history.replace(returnTo)
    
        } catch (err) {
            setError(err);
            throw err;
        } finally {
            setIsLoading(false);
        }

    }, [clearTokens, history]);

    /**
     * Saves the provided tokens, validates them, and stores them in either cookies or preferences
     * depending on the platform (native or not).
     * 
     * @param {Object} tokens - The tokens to save.
     * @param {string} tokens.access_token - The access token.
     * @param {string} tokens.id_token - The ID token.
     * @param {string} tokens.refresh_token - The refresh token.
     * @throws {Error} Throws error if tokens are invalid or the user cannot be validated.
     */
    const saveTokens = React.useCallback(async (tokens) => {

        const [
            accessToken, 
            idToken, 
            refreshToken
        ] = [KEYS.accessToken,KEYS.idToken,KEYS.refreshToken].map(key => tokens[key]);

        try {

            await acquireTokenLock();

            // Check if access, id and refresh tokens are valid
            const okToSave = Boolean(
                (!includeAccessToken    || accessToken) && 
                (!includeIdToken        || idToken) &&
                (!includeRefreshToken   || refreshToken)
            );
            if (!okToSave)
                throw new Error("Invalid token data");

            const tokenMap = {
                [KEYS.accessToken]  : includeAccessToken  ? accessToken  : null,
                [KEYS.idToken]      : includeIdToken      ? idToken      : null,
                [KEYS.refreshToken] : includeRefreshToken ? refreshToken : null,
                [KEYS.issuedAt]     : null,
                [KEYS.expiresAt]    : null
            };

            // parameters to validate
            const   token               = includeIdToken ? tokenMap[KEYS.idToken] : tokenMap[KEYS.accessToken],
                    expectedAudience    = includeIdToken ? clientId : null,
                    expectedAzp         = includeIdToken ? null : clientId;

            const payload = await verifyToken(token, expectedAudience, expectedAzp);
            if(!payload)
                throw new Error('Token cannot be validated')

            // Extract issuedAt and expiresAt from user data
            tokenMap[KEYS.issuedAt]          = payload.iat;
            tokenMap[KEYS.expiresAt]         = payload.exp;

            // For each in tokenMap, if isNative is true, use Preferences, else use cookies
            await Promise.all(
                Object
                    .entries(tokenMap)
                    .map(([key,value]) => (
                        _setValue(key,value)
                    ))
            );

            setAuthTokens(tokenMap);
            setUser(payload);
            setIsAuthenticated(true);

            return tokenMap;
            
        } catch (err) {
            releaseTokenLock(); // NOTE: Intentionally before clearTokens called, to prevent deadlock
            await clearTokens(); 
            throw err;
        } finally {
            releaseTokenLock(); // Ensure release
        }

    }, [_setValue, acquireTokenLock, clearTokens, clientId, includeAccessToken, includeIdToken, includeRefreshToken, releaseTokenLock, setAuthTokens, verifyToken]);

    /**
     * Refreshes the authentication tokens using the refresh token.
     * 
     * @returns {Promise<Object>} The result containing the new tokens, including the access token.
     * @throws {Error} Throws an error if the refresh token is missing, the request fails, or the response is invalid.
     */
    const refreshTokens = React.useCallback(async (refreshTokenOverride = undefined) => {

        if (!refreshTokenOverride && !authTokens?.[KEYS.refreshToken]){
            throw new Error("Missing refresh token");
        }

        try {
            setIsRefreshing(true);
            await acquireRefreshLock();
            const response = await fetch(endpointsMap.token, {
                method  : 'POST',
                headers : { 'Content-Type': 'application/x-www-form-urlencoded' },
                body    : new URLSearchParams({
                    grant_type      : 'refresh_token',
                    client_id       : clientId,
                    refresh_token   : refreshTokenOverride || authTokens?.[KEYS.refreshToken]
                })
            });
    
            // Check Reponse OK
            if (!response.ok)
                throw new Error(`Failed to refresh token: ${response.statusText}`);
    
            // Done
            const result = await response.json();
            if (!result[KEYS.accessToken])
                throw new Error(`Failed to refresh tokens: Missing ${KEYS.accessToken}`);

            return result;
        } catch (err) {
            setError(err);
            logError(err,"Error refreshing tokens");
            throw err;
        } finally {
            releaseRefreshLock();
            setIsRefreshing(false);
        }
        
    },[authTokens, clientId, endpointsMap.token, acquireRefreshLock, releaseRefreshLock])

    /**
     * Loads authentication tokens from storage (cookies or Preferences), verifies their validity, and stores them if valid.
     * 
     * @returns {Promise<void>} Resolves when the tokens are successfully loaded and validated.
     * @throws {Error} Throws an error if there is an issue loading or validating the tokens.
     */
    const loadTokens = React.useCallback(async () => {

        // Only allow if connected
        if (!isConnected)
            return;

        // Only allow loading once
        if(isLoaded)
            return;

        setIsLoading(true);
        try {

            // Load from cookies if isNative is false
            let tokens = Object.fromEntries(
                await Promise.all(
                    Object
                        .values(KEYS)
                        .map(
                            async (key) => ([
                                key, 
                                await _getValue(key)
                            ])
                        )
                )
            );

            // Conditions to perform loading algorithm
            const proceedToLoad = Boolean(
                (!includeAccessToken    || tokens[KEYS.accessToken] ) && 
                (!includeIdToken        || tokens[KEYS.idToken]) &&
                (!includeRefreshToken   || tokens[KEYS.refreshToken])
            );

            if(debug){
                console.log("DEBUG TOKENS:")
                console.log(tokens)
                console.log(proceedToLoad)
            }

            // Short Circuit
            if(!proceedToLoad)
                return;

            // Extract and Update the issuedAt and expiresAt;
            tokens[KEYS.issuedAt]   = tokens[KEYS.issuedAt]  ? parseInt(tokens[KEYS.issuedAt],    10) : null;
            tokens[KEYS.expiresAt]  = tokens[KEYS.expiresAt] ? parseInt(tokens[KEYS.expiresAt],   10) : null;

            // Check expiresAt is in the past
            // If so, try to refresh the tokens first
            if(useRefreshTokens){
                const threshold     = moment().add(10,'seconds');
                const expiresAt     = tokens[KEYS.expiresAt];
                const refreshToken  = tokens[KEYS.refreshToken];
                const shouldRefresh = Boolean( refreshToken && (!expiresAt || moment.unix(expiresAt).isSameOrBefore( threshold )));
                if(shouldRefresh){
                    const refreshedTokens = await refreshTokens(refreshToken); // w/ override
                    tokens = {
                        ...tokens,
                        ...refreshedTokens,
                        [KEYS.issuedAt]     : refreshedTokens[KEYS.issuedAt]    ?? null,
                        [KEYS.expiresAt]    : refreshedTokens[KEYS.expiresAt]   ?? null,
                    };
                }
            }
            
            // Verify access and id tokens
            tokens[KEYS.accessToken]    = includeAccessToken    && tokens[KEYS.accessToken]   && verifyToken(tokens[KEYS.accessToken], null, clientId) ? tokens[KEYS.accessToken]   : null;
            tokens[KEYS.idToken]        = includeIdToken        && tokens[KEYS.idToken]       && verifyToken(tokens[KEYS.idToken],     clientId, null) ? tokens[KEYS.idToken]       : null;
            tokens[KEYS.refreshToken]   = includeRefreshToken   && tokens[KEYS.refreshToken] ? tokens[KEYS.refreshToken] : null;

            await saveTokens(tokens);

        } catch (err) {
            setError(err);
            logError(err, "Error loading tokens");
            throw err
        } finally {
            setIsLoading(false);
            setIsLoaded(true);
        }
    }, [_getValue, clientId, debug, includeAccessToken, includeIdToken, includeRefreshToken, isConnected, isLoaded, refreshTokens, saveTokens, useRefreshTokens, verifyToken]);


    const updateRefreshNextMoment = React.useCallback( () => {
        const now               = moment();
        const nextRefreshAt     = now.add(REFRESH_INTERVAL_MS,'milliseconds')
        setRefreshingAt(nextRefreshAt);
    }, []);

    // Refresh and Update Tokens
    const refreshAndSaveTokens = React.useCallback(async () => {
        try {
            const newTokens = await refreshTokens();
            await saveTokens(newTokens);
            return true;
        } catch (err) {
            setError(err);
            logError(err, "Token refresh and failed");
            await clearTokens();
        } finally {
            updateRefreshNextMoment()
        }
        return false;
    },[clearTokens, refreshTokens, saveTokens, updateRefreshNextMoment])

    // Token refresh loop
    React.useEffect(() => {
        if (!useRefreshTokens || !authTokens?.[KEYS.refreshToken]|| !isConnected){
            return;
        }
        updateRefreshNextMoment()
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = setInterval(refreshAndSaveTokens, REFRESH_INTERVAL_MS);
        return () => {
            clearInterval(refreshIntervalRef.current);
        }

    }, [authTokens, isConnected, refreshAndSaveTokens, updateRefreshNextMoment, useRefreshTokens]);

    /**
     * Attempts to refresh and save tokens if the refresh threshold has been exceeded.
     *
     * This function:
     * - Runs only if connected and not already refreshing.
     * - Calculates a refresh threshold using the provided `currentMoment`.
     * - Checks if `refreshingAt` is a valid moment and is before or equal to the threshold.
     * - If so, triggers `refreshAndSaveTokens`.
     * - Uses functional `setState` to avoid overlapping executions.
     *
     * @param {moment.Moment} currentMoment - The current time, used to calculate the refresh threshold.
     *
     * Dependencies:
     * - `isConnected`: Boolean indicating if the client is currently connected.
     * - `refreshAndSaveTokens`: Async function to perform token refresh and save.
     * - `refreshingAt`: Moment object representing the last token refresh time.
     * - `debug`: Boolean to optionally enable console logging.
     * - `setIsRefreshingResetting`: State setter to guard against concurrent refresh attempts.
     * - `setError`: State setter for error reporting.
     * - `logError`: Logging function for error tracking.
     */
    const resetRefreshTokens = React.useCallback((currentMoment) => {

        if(!isConnected || !moment.isMoment(currentMoment) || !moment.isMoment(refreshingAt))
            return;

        const threshold   = currentMoment.clone().subtract(REFRESH_INTERVAL_MS, 'milliseconds');
        const shouldReset = refreshingAt.isSameOrBefore(threshold) 

        if(!shouldReset)
            return;

        setIsResettingRefreshTokens(alreadyRefreshing => {

            if(alreadyRefreshing) return alreadyRefreshing;
            
            // Run the async operation outside of setState
            const performReset = async () => {
                try {
                    const success = await refreshAndSaveTokens();
                    if (debug) 
                        console.log(`Force Refresh and Save Tokens: ${success ? "SUCCESS" : "FAILURE"}`);
                } catch (err) {
                    setError(err);
                    logError(err, "Error force refresh and save tokens");
                } finally {
                    setIsResettingRefreshTokens(false);
                }
            };

            // Trigger the async reset logic
            performReset();

            // Return the new state value to prevent further resets
            return true;
        });
        
    },[debug, isConnected, refreshAndSaveTokens, refreshingAt]);

    // Execute the Reset Refresh Tokens Routine
    React.useEffect(() => {
        resetRefreshTokens(reseeingRefreshMoment)
    }, [reseeingRefreshMoment, resetRefreshTokens])

    /**
     * Attempts to silently retrieve the access token by checking if it needs to be refreshed.
     * If the token is about to expire (in less than 60 seconds), it will attempt to refresh the token.
     * If the token is expired, it will clear the tokens and throw an error.
     * If the token is still valid, it returns the existing access token.
     *
     * @returns {Promise<string>} The access token if it is valid or refreshed successfully.
     * @throws {Error} Throws an error if the refresh token has expired or if there is an issue during the refresh.
     */
    const getAccessTokenSilently    = React.useCallback(async () => {

        try {

            // Current Tokens
            const existingTokens = authTokensRef.current;

            if (!existingTokens?.[KEYS.refreshToken] || !existingTokens?.[KEYS.expiresAt]) {
                throw new Error("Missing refresh token or expiration info not available");
            }
        
            const currentTime   = Math.floor(moment().unix()); // current time in seconds
            const timeLeft      = existingTokens?.[KEYS.expiresAt] - currentTime;

            // Clear if Expired
            if(debug && timeLeft <= 0){
                console.warn("Access token has expired - attempty refresh ...");
            }

            // If token is expired or about to expire in less than 60 seconds, refresh
            if (timeLeft < 60) {
                const newTokens = await refreshTokens();
                await saveTokens(newTokens);
                return newTokens[KEYS.accessToken];
            } 

            // If no access token to fall back on, raise error
            if(!existingTokens[KEYS.accessToken])
                throw new Error("Missing access token")
            
            // If token is still valid enough, use it
            return existingTokens[KEYS.accessToken];
    
        } catch (err) {
            setError(err);
            logError(err, "Token refresh failed");
            await clearTokens();
            throw err;
        }
    
    }, [debug, refreshTokens, saveTokens, clearTokens]);

    /**
     * Exchanges an authorization code for tokens.
     *
     * @param {string} code The authorization code received from the authorization server.
     * @returns {Promise<Object>} A promise that resolves to the response data containing tokens.
     * @throws {Error} Throws an error if the token exchange fails.
     */
    const exchangeCodeForToken = React.useCallback( async (code)  => {

        const { scope, audience } = authorizationParams || {}; // Destructure once for cleaner code
        
        const queryParamsArgs   = {
            grant_type   : 'authorization_code',
            client_id    : clientId,
            redirect_uri : isNative ? nativeCallbackUri : rootUrl,
            code_verifier: await _getValue(APP_PKCE_VERIFIER_KEY),
            scope        : scope || '',
            code         : code,
            ...(audience && { audience }),
        }
        const queryParams = new URLSearchParams(queryParamsArgs);
        
        let response;
        try {
            response = await fetch(endpointsMap.token, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded'
                },
                body: queryParams.toString() // important: convert to url-encoded string
            });
        
            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.error_description || 'Token exchange failed');
            }
        
            return data;
            
        } catch (err) {
            setError(err)
            logError(err, 'Failed to exchange code for token');
            throw err;
        }
    }, [_getValue, authorizationParams, clientId, endpointsMap.token, isNative]);

    /**
     * Handles the login process by exchanging an authorization code for tokens 
     * and saving them to persistent storage.
     * 
     * This function calls `exchangeCodeForToken` to exchange the provided 
     * authorization code for access, ID, and refresh tokens, then saves these tokens
     * using the `saveTokens` function.
     * 
     * @param {string} code - The authorization code received after user login.
     * 
     * @returns {Promise<void>} A promise that resolves when the tokens are successfully saved.
     * 
     * @throws {Error} Throws an error if the `exchangeCodeForToken` or `saveTokens` process fails.
     */
    const _loginWithCode = React.useCallback(async (code) => {
        const tokens = await exchangeCodeForToken(code)
        const subset = pick(tokens, [KEYS.accessToken,KEYS.idToken,KEYS.refreshToken]);
        await saveTokens(subset)
    }, [exchangeCodeForToken, saveTokens])

    /**
     * Initiates the login process by opening a popup or native browser for authentication.
     * 
     * This function handles the creation of a PKCE (Proof Key for Code Exchange) challenge 
     * to protect the authorization code flow, generates a secure state parameter for CSRF protection, 
     * and then opens the authentication URL either in a native browser (for mobile apps) 
     * or a web popup. Once the user successfully logs in and the popup is redirected to the callback URI,
     * it exchanges the authorization code for tokens.
     * 
     * @param {Object} [options={}] - Options to configure the login flow.
     * @param {Object} [options.appState] - Application-specific state to be saved and used after login.
     * @returns {Promise<Object>} A promise that resolves with the token data once the login process completes.
     * @throws {Error} Throws an error if the popup is closed by the user or if the state or code validation fails.
     */
    const loginWithPopup = React.useCallback(async ({ options = {} } = {}) => {

        clearProcessedCodes(); // Clear Existing Processed Codes

        const appState = { returnTo : window.location.pathname, ...options?.appState};
        await _setValue(APP_STATE_KEY, JSON.stringify(appState));
    
        // Generate a CSRF-safe state
        const pkceStateValue    = generateRandomString(APP_PKCE_STATE_LENGTH);
        const pkceCodeVerifier  = generateRandomString(APP_PKCE_VERIFIER_LENGTH);
        const pkceCodeChallenge = await generateCodeChallenge(pkceCodeVerifier);
    
        await _setValue(APP_PKCE_STATE_KEY, pkceStateValue);
        await _setValue(APP_PKCE_VERIFIER_KEY, pkceCodeVerifier);
    
        const { scope, audience } = authorizationParams || {};
        const queryParamsArgs = {
            client_id               : clientId,
            redirect_uri            : isNative ? nativeCallbackUri : rootUrl,
            response_type           : 'code',
            prompt                  : 'login',
            scope                   : scope || '',
            audience                : audience || '',
            state                   : pkceStateValue,
            code_challenge          : pkceCodeChallenge,
            code_challenge_method   : 'S256',
        }
        const queryParams = new URLSearchParams(queryParamsArgs);
    
        const authUrl = `${endpointsMap.authorize}?${queryParams.toString()}`;
    
        if (isNative) {
            try {
                await Browser.open({ url: authUrl });
                // Native flow continues in the callback URI handler (e.g., deep link)
                return;
            } catch (err) {
                setError(err);
                logError(err,'Failed to open browser for login');
                throw err;
            }
        }
    
        // Web popup fallback
        const width = 400;
        const height = 600;
        const left = window.screenX + (window.innerWidth - width) / 2;
        const top = window.screenY + (window.innerHeight - height) / 2;
        const popup = window.open(authUrl, 'auth-popup', [
            `width=${width}`,
            `height=${height}`,
            `left=${left}`,
            `top=${top}`,
            'resizable=no',
            'scrollbars=no',
            'status=no',
            'location=no',
            'toolbar=no',
            'menubar=no',
        ].join(','));
    
        return new Promise((resolve, reject) => {
            const interval = setInterval(() => {
                try {
                    if (!popup || popup.closed) {
                        clearInterval(interval);
                        reject(new Error('Popup closed by user'));
                        return;
                    }
    
                    const popupUrl = popup.location.href;
                    const url = new URL(popupUrl);
    
                    const code = url.searchParams.get('code');
                    const returnedState = url.searchParams.get('state');
    
                    if (code && returnedState) {
                        if (returnedState !== pkceStateValue) {
                            throw new Error('State mismatch');
                        }
    
                        clearInterval(interval);
                        popup.close();

                        _loginWithCode(code)
                            .then(resolve)
                            .catch(reject);
                    }
                } catch (err) {
                    // Will throw a cross-origin error until redirected to same-origin
                }
            }, 500);
        });
    }, [_loginWithCode, _setValue, authorizationParams, clearProcessedCodes, clientId, endpointsMap.authorize, isNative]);

    /**
     * Initiates the login process by performing a full-page redirect or opening a native browser 
     * for authentication, based on the platform (native or web). It generates a PKCE (Proof Key 
     * for Code Exchange) challenge, a CSRF-safe state, and constructs the authorization URL 
     * before redirecting or opening the login flow.
     * 
     * @param {Object} [options={}] - Options to configure the login flow.
     * @param {Object} [options.appState] - Application-specific state to be saved and used after login.
     * 
     * @returns {void} This function performs a redirect or opens a native browser window. It does not return a value.
     * 
     * @throws {Error} Throws an error if the native browser fails to open (only in native flow).
     */
    const loginWithRedirect = React.useCallback(async ({ options = {} } = {}) => {

        clearProcessedCodes(); // Clear Existing Processed Codes

        const appState = { returnTo : window.location.pathname, ...options?.appState};
        await _setValue(APP_STATE_KEY, JSON.stringify(appState))

        // Generate a CSRF-safe state
        const pkceStateValue    = generateRandomString(APP_PKCE_STATE_LENGTH);
        const pkceCodeVerifier  = generateRandomString(APP_PKCE_VERIFIER_LENGTH)
        const pkceCodeChallenge = await generateCodeChallenge(pkceCodeVerifier);

        await _setValue(APP_PKCE_STATE_KEY,pkceStateValue);
        await _setValue(APP_PKCE_VERIFIER_KEY, pkceCodeVerifier);

        const args = [
            `client_id=${clientId}`,
            `redirect_uri=${isNative ? nativeCallbackUri : rootUrl}`,
            `response_type=code`,
            `prompt=login`,
            `scope=${encodeURIComponent(authorizationParams?.scope || '')}`,
            `audience=${encodeURIComponent(authorizationParams?.audience || '')}`,
            `state=${pkceStateValue}`,
            `code_challenge=${pkceCodeChallenge}`,
            `code_challenge_method=S256`
        ];
        
        // Build Auth URL
        const authUrl = `${endpointsMap.authorize}?${args.join('&')}`;
        
        // Open the browser and add listener
        if(isNative){
            Browser
                .open({ url: authUrl })
                .catch((err) => {
                    setError(err);
                    logError(err,'Failed to open browser');
                });
        
        // Web: perform a full-page redirect
        }else{
            window.location.href = authUrl;
        }
        
    }, [_setValue, authorizationParams?.audience, authorizationParams?.scope, clearProcessedCodes, clientId, endpointsMap.authorize, isNative]);

    React.useEffect(() => {

        const parsed        = new URL(window.location.href);
        const code          = parsed.searchParams.get('code');
        const state         = parsed.searchParams.get('state');

        const clearUrlParams = () => {
            parsed.searchParams.delete('code');
            parsed.searchParams.delete('state');
            history.replace(parsed.pathname + parsed.search);
        };

        const processRedirect = async () => {

            if (!code || !state) {
                return
            }

            if(processedCodeRef.current.has(code)){
                return;
            }

            // Check code used, code is single use
            processedCodeRef.current.set(code, moment());
            
            try {
                await _loginWithCode(code);
            } catch (err) {
                setError(err)
                logError(err,'Exchange code for token error');
            }

            try {
                const appStateString = await _getValue(APP_STATE_KEY)
                if (appStateString) {
                    const appState = JSON.parse(appStateString);
                    await handleRedirectCallback(appState);
                }
            } catch (err) {
                setError(err);
                logError(err,'Auth callback error');
            } finally {
                _deleteValue(APP_STATE_KEY)
                clearUrlParams(); // Prevent re-processing
            }
            
        };

        processRedirect();

    }, [_deleteValue, _getValue, _loginWithCode, handleRedirectCallback, history, isNative]);

    // Remove Stale Processed Codes
    React.useEffect(() => {
        if(isConnected){
            const interval = setInterval(cleanupProcessedCodes, REFRESH_INTERVAL_MS)
            return () => {
                clearInterval(interval);
            }
        }
    },[isConnected, cleanupProcessedCodes])

    React.useEffect(() => {

        if(!isNative)
            return;

        // Setup listener cleanup stub
        let removeListener = null;
        
        // Handle redirect from Auth0 back to app
        const handleRedirect = async ({ url }) => {
        
            if (url?.startsWith(nativeCallbackUri)) {

                const parsed            = new URL(url);
                const code              = parsed.searchParams.get('code');
                const returnedState     = parsed.searchParams.get('state');
                const pkceStateValue    = await _getValue(APP_PKCE_STATE_KEY);
            
                try {
                    if (returnedState !== pkceStateValue) {
                        throw new Error('State mismatch - potential CSRF');
                    }    
                } catch (err) {
                    setError(err);
                    logError(err,"Security error")
                    return;
                }
                
                // TODO: Exchange the code for a token
                if (code) {
                    try {
                        await _loginWithCode(code);
                    } catch (err) {
                        setError(err)
                        logError(err,'Error exchanging code for token');
                    } finally {
                        try {
                            await Browser.close();
                        } catch (err) {
                            logError('Error Closing Browser');
                        }
                    }
                }
            }
        };

        CapApp
            .addListener('appUrlOpen', handleRedirect)
            .then(listener => {
                removeListener = listener.remove 
            });

        return () => {
            if (removeListener && typeof removeListener === 'function') {
                removeListener();
                removeListener = null;
            }
        };
    }, [_getValue, _loginWithCode, isNative])

    // Load tokens on mount or when function changes
    React.useEffect(() => {
        loadTokens()
            .catch((err) => {
                logError(err,"Load Tokens Effect")
            });    
    }, [loadTokens]);

    const value = {
        error,
        user,
        isRefreshing,
        isResettingRefreshTokens,
        isLoading,
        isLoaded,
        isAuthenticated,
        authTokens,
        issuedAt, 
        expiresAt,
        refreshingAt,
        setAuthTokens,
        saveTokens,
        getAccessTokenSilently,
        loginWithRedirect,
        loginWithPopup,
        logout
    }

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    )
}

// Auth Consumer
const AuthConsumer = ({children}) => {
    return (
        <AuthContext.Consumer>
            {(context) => {
                if (context === undefined) {
                    throw new Error('AuthConsumer must be used within AuthProvider');
                }
                return children(context)
            }}
        </AuthContext.Consumer>
    )
}

// useAuth Hook
const useAuth = () => {
    const context = React.useContext(AuthContext);
    if(context === undefined)
        throw new Error('useAuth must be used within AuthProvider');
    return context;
}

// HOC to wrap components in AuthProvider
const withAuth = (Component) => (props) => (
    <AuthProvider>
        <Component {...props} />
    </AuthProvider>
);

export {
    AuthProvider,
    AuthConsumer,
    useAuth,
    withAuth
}